import { FC, ReactNode } from "react";
import Permissions from "../../constants/permissions";
import useAuth, { User } from "../../context/UserContext";
import { ToDashboard } from "../RouteHelpers";

interface IfPermissionProps {
  permission: Permissions;
  children: ReactNode;
}

const IfPermission: FC<IfPermissionProps> = ({ permission, children }) => {
  const { user } = useAuth();
  if (hasPermission(permission, user)) {
    return <>{children}</>;
  } else {
    return null;
  }
};

interface RequirePermissionProps {
  /**
   * React nodes to be rendered as children of this component
   */
  children: ReactNode;

  /**
   * Indicates the level of permission required to access children elements. The default permission is "viewer"
   */
  permission?: Permissions;
}

const RequirePermission: FC<RequirePermissionProps> = ({ children, permission = Permissions.View }) => {
  const { user } = useAuth();
  return hasPermission(permission, user) ? <>{children}</> : <ToDashboard />;
};

const hasPermission = (requiredPermission: Permissions, userContext: User) => {
  if (!userContext.isAuthenticated) {
    return false;
  }
  if (!userContext.permissions) {
    return false;
  }
  const permissions = userContext.permissions || [];
  return !!permissions.find((r) => r === requiredPermission);
};

const downloadSummaryEnabled = (userContext: User): boolean => {
  const actualUrlName = userContext.default_url_name;
  const elem = userContext.accounts?.filter((account) => account.urlName === actualUrlName)[0];
  return elem?.show_download_summary || false;
};

export { downloadSummaryEnabled, hasPermission, IfPermission, RequirePermission };
